import { BuildingOUT } from "api-client";
import clsx from "clsx";
import SearchInput from "components/SearchInput";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { buildingStore } from "store/Building/BuildingStore";
import { useNavigate } from "react-router";
import { isTabletSize } from "utils";
import { globalStore } from "store/GlobalStore";
import BuildingLogo from "features/SidebarMenu/components/BuildingLogo";
import Tooltip from "components/Tooltip";
import { ROUTES_ENUM } from "routes/consts";

function BuildingsList({
  buildings,
  className,
  ...props
}: {
  buildings: BuildingOUT[];
  className?: string;
  loading?: boolean;
  onClickBuilding?: (building: BuildingOUT) => void;
  containerClassName?: string;
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const navigate = useNavigate();
  const { currentBuilding } = buildingStore;
  const { t } = useTranslation();

  const filteredList = searchTerm.length
    ? buildings.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : buildings;

  const showBuildingDetail = (building: BuildingOUT) => {
    props.onClickBuilding?.(building);
    if (buildingStore.currentBuilding?.id !== building.id) {
      buildingStore.setCurrentBuilding(building);
    }
    const buildingURL = building.license
      ? ROUTES_ENUM.BUILDINGS
      : ROUTES_ENUM.BUILDING_NO_LICENSE;
    navigate(`/${buildingURL}/${building.id}`);
    if (isTabletSize()) {
      globalStore.setSidebarOpened(false);
    }
  };

  function content() {
    return (
      <div className={props.containerClassName}>
        {buildings.length > 0 ? (
          <SearchInput
            containerClassName="!mb-3"
            placeholder={t("buildingPassport.searchForBuilding")}
            onChange={(value) => setSearchTerm(value)}
          />
        ) : null}
        {buildings.length === 0 ? (
          <div className="text-sm text-gray-500 py-1">
            {t("buildingPassport.noBuildingExist")}
          </div>
        ) : null}
        {filteredList.map((building: BuildingOUT) => (
          <div
            key={building.id}
            className={clsx(
              "flex p-2 mb-0.5 items-center cursor-pointe hover:bg-indigo-50 rounded transition cursor-pointer",
              {
                "bg-indigo-50 text-indigo-700":
                  currentBuilding?.id === building.id,
              }
            )}
            onClick={() => showBuildingDetail(building)}
          >
            <BuildingLogo building={building} />
            <Tooltip
              truncate
              className="pl-2 whitespace-nowrap font-medium text-sm max-w-[260px]"
            >
              {building?.name}
            </Tooltip>
          </div>
        ))}
        {searchTerm.length && !filteredList.length ? (
          <div className="text-sm text-gray-500 pb-1">
            {t("buildingPassport.noBuildingFound")}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "px-4 pb-4 flex flex-col flex-grow overflow-y-auto max-h-[calc(100vh-455px)]",
        className
      )}
    >
      {props.loading ? skeleton() : content()}
    </div>
  );
}

export default observer(BuildingsList);

function skeleton() {
  return (
    <div className="animate-pulse flex flex-grow flex-col pt-2">
      <div className="h-[35px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1"></div>
      <div className="h-[35px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1"></div>
      <div className="h-[35px] w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-1"></div>
    </div>
  );
}
